import React, { useState, useRef } from "react";
import Title from "../components/Title";
import { SimpleGrid } from "@chakra-ui/react";
import DictionaryHeader from "../components/Dictionary/DictionaryHeader";
import DictionaryBlock from "../components/Dictionary/DictionaryBlocks";
import DictionaryView from "../components/Dictionary/DictionaryView";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getDictionaryStats, callWithAuth, getUserInfo } from "../api/Api";
import ReviewInsights from "../components/Review/ReviewInsights.tsx";
import { useTrackViewedWords } from "../hooks/posthog/useTrackViewedWords";
import { useTrackViewedPhrases } from "../hooks/posthog/useTrackViewedPhrases";
import { LoadingPage } from "./LoadingPage";

const DICTIONARY_STATS_QUERY_KEY = "dictionaryStats";
const USER_INFO_QUERY_KEY = "userInfo";
const KNOWN_PHRASES_QUERY_KEY = "knownPhrases";
const LEARNING_PHRASES_QUERY_KEY = "learningPhrases";
const KNOWN_WORDS_QUERY_KEY = "knownWords";
const LEARNING_WORDS_QUERY_KEY = "learningWords";

const DictionaryPage: React.FC = () => {
  const [viewType, setViewType] = useState<"Words" | "Phrases" | null>(null);
  const wordListRef = React.useRef<{ refreshQueries: () => void }>(null);
  const isListDirty = useRef<boolean>(false);
  const trackViewedWords = useTrackViewedWords();
  const trackViewedPhrases = useTrackViewedPhrases();

  const releventQueryKeys = [
    KNOWN_WORDS_QUERY_KEY,
    LEARNING_WORDS_QUERY_KEY,
    DICTIONARY_STATS_QUERY_KEY,
    USER_INFO_QUERY_KEY,
    KNOWN_PHRASES_QUERY_KEY,
    LEARNING_PHRASES_QUERY_KEY,
  ];

  const dictionaryStatsQuery = useQuery({
    queryKey: [DICTIONARY_STATS_QUERY_KEY],
    queryFn: () => {
      return callWithAuth(getDictionaryStats);
    },
  });

  const userInfoQuery = useQuery({
    queryKey: [USER_INFO_QUERY_KEY],
    queryFn: () => {
      return callWithAuth(getUserInfo);
    },
  });

  const queryClient = useQueryClient();

  // Add loading state
  if (dictionaryStatsQuery.isPending || userInfoQuery.isPending) {
    return <LoadingPage showText={false} />;
  }

  const firstName = userInfoQuery.data?.firstName || "";

  const handleWordsClick = async () => {
    if (isListDirty.current) {
      await queryClient.invalidateQueries({
        queryKey: releventQueryKeys,
      });
      await dictionaryStatsQuery.refetch();
      wordListRef.current?.refreshQueries();
      isListDirty.current = false;
    }
    setViewType("Words");
    trackViewedWords();
  };

  const handlePhrasesClick = async () => {
    if (isListDirty.current) {
      await queryClient.invalidateQueries({
        queryKey: releventQueryKeys,
      });
      await dictionaryStatsQuery.refetch();
      wordListRef.current?.refreshQueries();
      isListDirty.current = false;
    }
    setViewType("Phrases");
    trackViewedPhrases();
  };

  const handleCloseClick = async () => {
    if (isListDirty.current) {
      await queryClient.invalidateQueries({
        queryKey: releventQueryKeys,
      });
      await dictionaryStatsQuery.refetch();
      wordListRef.current?.refreshQueries();
      isListDirty.current = false;
    }
    setViewType(null);
  };

  const handleWordOrPhraseStatusUpdate = async () => {
    isListDirty.current = true;
    await dictionaryStatsQuery.refetch();
  };

  // Include all dictionary stats with defaults
  const dictionaryStats = dictionaryStatsQuery.data || {
    knownWordCount: 0,
    learningWordCount: 0,
    knownPhraseCount: 0,
    learningPhraseCount: 0,
  };

  // Combine word counts for the header
  const totalWordsCount =
    dictionaryStats.knownWordCount + dictionaryStats.learningWordCount;

  // Use phrase counts from dictionary stats
  const totalPhrasesCount =
    dictionaryStats.knownPhraseCount + dictionaryStats.learningPhraseCount;

  return (
    <div className="flex flex-col justify-start w-full min-h-full pb-8 gap-4">
      <Title text="🏆 Daily Review" className="pt-14" />
      <ReviewInsights />
      <Title text={firstName + "'s Dictionary"} />
      <DictionaryHeader
        onWordsClick={handleWordsClick}
        onPhrasesClick={handlePhrasesClick}
        wordsCount={totalWordsCount}
        phrasesCount={totalPhrasesCount}
      />
      {viewType === null ? (
        <>
          <Title text="Dictionary Categories" />
          <SimpleGrid columns={2} spacing={3}>
            <DictionaryBlock
              icon="📚"
              title="Words"
              subtitle={
                <span>
                  You have{" "}
                  <span className="font-bold text-[var(--color-blue)]">
                    {totalWordsCount}
                  </span>{" "}
                  words in your dictionary
                </span>
              }
              onClick={handleWordsClick}
            />
            <DictionaryBlock
              icon="📗"
              title="Phrases"
              subtitle={
                <span>
                  You have{" "}
                  <span className="font-bold text-[var(--color-green)]">
                    {totalPhrasesCount}
                  </span>{" "}
                  phrases in your dictionary
                </span>
              }
              onClick={handlePhrasesClick}
            />
          </SimpleGrid>
        </>
      ) : (
        <DictionaryView
          ref={wordListRef}
          type={viewType}
          onClose={handleCloseClick}
          onStatusUpdate={handleWordOrPhraseStatusUpdate}
          phraseQueryKeys={{
            knownPhrasesKey: KNOWN_PHRASES_QUERY_KEY,
            learningPhrasesKey: LEARNING_PHRASES_QUERY_KEY,
          }}
          dictionaryStats={dictionaryStats}
        />
      )}
    </div>
  );
};

export default DictionaryPage;
